package fr.frux.feature.treedetail

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Adjust
import androidx.compose.material.icons.outlined.Block
import androidx.compose.material.icons.outlined.CheckCircle
import androidx.compose.material.icons.outlined.DoneAll
import androidx.compose.material.icons.outlined.HelpOutline
import androidx.compose.material.icons.outlined.HourglassTop
import androidx.compose.material.icons.outlined.RadioButtonUnchecked
import androidx.compose.material.icons.outlined.Schedule
import androidx.compose.material.icons.outlined.Verified
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.color
import fr.frux.core.designsystem.theme.label
import fr.frux.core.designsystem.theme.onColor
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_certainty_certain
import fr.frux.feature.treedetail.resources.treedetail_certainty_probable
import fr.frux.feature.treedetail.resources.treedetail_certainty_to_confirm
import fr.frux.feature.treedetail.resources.treedetail_default_username
import fr.frux.feature.treedetail.resources.treedetail_header_added_by
import fr.frux.feature.treedetail.resources.treedetail_header_added_by_me
import fr.frux.shared.domain.model.Certainty
import org.jetbrains.compose.resources.stringResource

/** Hauteur minimale et taille d'icône des pastilles de l'en-tête (canvas `Fiche`). */
private val PILL_HEIGHT = 32.dp
private val PILL_ICON = 18.dp

/**
 * Zone Identité : pastilles statut + certitude, ligne de passage humain (#420, ADR 0025), nom
 * d'espèce `displayLarge`, ligne « Ajouté par ».
 */
@Composable
internal fun TreeHeader(tree: TreeDetail) {
    val creatorName =
        tree.creatorUsername?.takeIf { it.isNotBlank() } ?: stringResource(Res.string.treedetail_default_username)
    Column(Modifier.padding(horizontal = 20.dp)) {
        Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
            StatusPill(tree.status)
            CertaintyPill(tree.certainty)
        }
        // #426 : masquée quand l'arbre est contesté, le bandeau de signalement dit déjà l'essentiel
        // et la ligne de passage se contredisait avec lui (cf. shouldShowSightingLine).
        if (shouldShowSightingLine(tree.disputed)) {
            Spacer(Modifier.height(6.dp))
            Text(
                text = tree.sighting.label(tree.lastObservedAtMillis, tree.sightingNowMillis),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        Spacer(Modifier.height(10.dp))
        Text(
            text = tree.speciesName,
            style = MaterialTheme.typography.displayLarge,
            color = MaterialTheme.colorScheme.primary,
        )
        Spacer(Modifier.height(2.dp))
        Text(
            text =
                when {
                    tree.isOwnTree -> stringResource(Res.string.treedetail_header_added_by_me)
                    tree.importSource != null -> stringResource(importSourceLabel(tree.importSource))
                    else -> stringResource(Res.string.treedetail_header_added_by, creatorName)
                },
            style = MaterialTheme.typography.bodyLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

/** Pastille statut : `FruxStatus.color()` en fond plein, texte et icône on-color (spec D). */
@Composable
private fun StatusPill(status: FruxStatus) {
    Row(
        modifier =
            Modifier
                .clip(RoundedCornerShape(50))
                .background(status.color())
                .heightIn(min = PILL_HEIGHT)
                .padding(start = 10.dp, end = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Icon(
            imageVector = status.icon(),
            contentDescription = null,
            modifier = Modifier.size(PILL_ICON),
            tint = status.onColor(),
        )
        Text(
            text = status.label,
            style = MaterialTheme.typography.labelLarge,
            color = status.onColor(),
        )
    }
}

private fun FruxStatus.icon(): ImageVector =
    when (this) {
        FruxStatus.Available -> Icons.Outlined.CheckCircle
        FruxStatus.Flowering -> Icons.Outlined.HourglassTop
        FruxStatus.Picked -> Icons.Outlined.DoneAll
        FruxStatus.OffSeason -> Icons.Outlined.Schedule
        FruxStatus.Unavailable -> Icons.Outlined.Block
        FruxStatus.Unknown -> Icons.Outlined.RadioButtonUnchecked
    }

/**
 * Pastille certitude (canvas `Fiche`) : toujours `surfaceVariant` et `onSurface`, libellé court et
 * icône qui porte le niveau (Verified, Adjust, HelpOutline). Plus de compteur de confirmations
 * depuis le retrait du vote d'identification (ADR 0025).
 */
@Composable
internal fun CertaintyPill(certainty: Certainty) {
    val container = MaterialTheme.colorScheme.surfaceVariant
    val content = MaterialTheme.colorScheme.onSurface
    val icon =
        when (certainty) {
            Certainty.CERTAIN -> Icons.Outlined.Verified
            Certainty.PROBABLE -> Icons.Outlined.Adjust
            Certainty.TO_CONFIRM -> Icons.Outlined.HelpOutline
        }
    val shortLabel =
        when (certainty) {
            Certainty.CERTAIN -> stringResource(Res.string.treedetail_certainty_certain)
            Certainty.PROBABLE -> stringResource(Res.string.treedetail_certainty_probable)
            Certainty.TO_CONFIRM -> stringResource(Res.string.treedetail_certainty_to_confirm)
        }
    Row(
        modifier =
            Modifier
                .clip(RoundedCornerShape(50))
                .background(container)
                .heightIn(min = PILL_HEIGHT)
                .padding(start = 10.dp, end = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            modifier = Modifier.size(PILL_ICON),
            tint = content,
        )
        Text(
            text = shortLabel,
            style = MaterialTheme.typography.labelLarge,
            color = content,
        )
    }
}
